import React, { useState, useEffect } from "react";
// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Paper from "@material-ui/core/Paper";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

// Icons
import Search from "@material-ui/icons/Search";
import TrendingUp from "@material-ui/icons/TrendingUp";
import ShoppingCart from "@material-ui/icons/ShoppingCart";
import CheckCircle from "@material-ui/icons/CheckCircle";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import MoreVert from "@material-ui/icons/MoreVert";
import Person from "@material-ui/icons/Person";

// core components
import DoobiHeader from "components/Headers/DoobiHeader";
import config from "../../../config";

const useStyles = makeStyles((theme) => ({
    cardRoot: {
        marginBottom: "1.5rem",
        borderRadius: "15px",
        boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .15)",
    },
    statCard: {
        borderRadius: "15px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "1rem",
        transition: "transform 0.2s",
        "&:hover": {
            transform: "translateY(-4px)"
        }
    },
    iconShape: {
        width: "48px",
        height: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        marginBottom: "1rem",
        backgroundColor: theme.palette.primary.main,
        color: "#fff"
    },
    filterBox: {
        padding: "1.5rem",
        backgroundColor: "#fff",
        borderRadius: "15px",
        marginBottom: "2rem",
        boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .1)",
    },
    tableContainer: {
        borderRadius: "15px",
        overflow: "hidden"
    },
    statusChip: {
        fontWeight: "bold",
        borderRadius: "6px"
    },
    priceCell: {
        fontWeight: "bold",
        color: theme.palette.primary.main
    },
    orderItemRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "12px 14px",
        border: "1px solid #e9ecef",
        borderRadius: "10px",
        marginBottom: "10px",
        backgroundColor: "#fafbff"
    },
    orderItemMeta: {
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "8px"
    },
    orderItemBadge: {
        padding: "4px 10px",
        borderRadius: "999px",
        backgroundColor: "#eef2ff",
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: "0.75rem"
    },
    orderItemPriceNote: {
        padding: "4px 10px",
        borderRadius: "999px",
        backgroundColor: "#f1f3f5",
        color: "#525f7f",
        fontWeight: 600,
        fontSize: "0.75rem"
    }
}));

function PosSales() {
    const classes = useStyles();
    const theme = useTheme();

    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [branchFilter, setBranchFilter] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: 'placedOn', direction: 'desc' });

    // Customer Dialog State
    const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Anchor for Status Menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeOrderId, setActiveOrderId] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem("token");
            const tenantId = localStorage.getItem("tenantId");

            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order`, {
                headers: {
                    'Authorization': token || '',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });
            const data = await response.json();
            if (response.ok) {
                setOrders(Array.isArray(data) ? data : (data.data || []));
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            const token = localStorage.getItem("token");
            const tenantId = localStorage.getItem("tenantId");

            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order/${orderId}/status?status=${newStatus}`, {
                method: 'PUT',
                headers: {
                    'Authorization': token || '',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });

            if (response.ok) {
                fetchOrders(); // Refresh table
                setDetailsOpen(false);
            } else {
                alert("Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
        }
        setAnchorEl(null);
    };

    const handleViewDetails = async (order) => {
        try {
            const token = localStorage.getItem("token");
            const selectedCountry = localStorage.getItem("selectedCountry");
            const tenantId = localStorage.getItem("tenantId");
            const response = await fetch(`${config.WS_BASE_URL}api/walk-in-order/${order.id}`, {
                headers: {
                    'Authorization': token || '',
                    'Country': selectedCountry || '1',
                    'X-Tenant-Key': tenantId || 'main'
                }
            });
            const data = await response.json();
            if (response.ok) {
                setSelectedOrder(data);
                setDetailsOpen(true);
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
            // Fallback to local data if needed
            setSelectedOrder(order);
            setDetailsOpen(true);
        }
    };

    const getCustomer = (order) => {
        return order.walkInCustomer || order.walkInCustomerByWalkInCustomerId;
    };

    const filteredOrders = orders.filter(order => {
        const customer = getCustomer(order);
        const matchesSearch = order.id.toString().includes(searchTerm) ||
            (customer?.firstName && customer.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (customer?.mobileNumber && customer.mobileNumber.includes(searchTerm));
        const matchesStatus = statusFilter === "all" || order.status.toString() === statusFilter;
        const matchesBranch = branchFilter === "all" || (order.createdBy || order.username) === branchFilter;

        let matchesDate = true;
        if (startDate || endDate) {
            const orderDate = new Date(order.placedOn).toISOString().split('T')[0];
            if (startDate && orderDate < startDate) matchesDate = false;
            if (endDate && orderDate > endDate) matchesDate = false;
        }

        return matchesSearch && matchesStatus && matchesDate && matchesBranch;
    }).sort((a, b) => {
        if (sortConfig.key) {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Handle nested properties or specific keys
            if (sortConfig.key === 'customer') {
                const customerA = getCustomer(a);
                const customerB = getCustomer(b);
                aValue = customerA?.firstName || '';
                bValue = customerB?.firstName || '';
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
        }
        return 0;
    });

    const analytics = {
        totalSales: filteredOrders.reduce((acc, o) => acc + (o.total || 0), 0),
        totalOrders: filteredOrders.length,
        completedOrders: filteredOrders.filter(o => o.status === 3).length,
        pendingOrders: filteredOrders.filter(o => [0, 1, 2].includes(o.status)).length,
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const branches = [...new Set(orders.map(o => o.createdBy || o.username).filter(Boolean))];

    const getStatusInfo = (status) => {
        switch (status) {
            case 0: return { label: "Placed", color: "#fb6340", bgColor: "#fff1ee" };
            case 1: return { label: "Washing", color: "#11cdef", bgColor: "#e0f7fa" };
            case 2: return { label: "Washed", color: "#8965e0", bgColor: "#f3f0fb" };
            case 3: return { label: "Completed", color: "#2dce89", bgColor: "#e7fcf1" };
            case 4: return { label: "Cancelled", color: "#f5365c", bgColor: "#fee7eb" };
            default: return { label: "Unknown", color: "#adb5bd", bgColor: "#f8f9fe" };
        }
    };

    return (
        <Box style={{ backgroundColor: "#f8f9fe", minHeight: "100vh" }}>
            <DoobiHeader section="POS Sales" subsection="Manage your POS sales history" />
            <Container maxWidth={false} component={Box} marginTop="-6rem">
                {/* Analytics Segment */}
                <Grid container spacing={3} style={{ marginBottom: '2rem' }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statCard}>
                            <Box className={classes.iconShape}>
                                <TrendingUp />
                            </Box>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>TOTAL REVENUE</Typography>
                            <Typography variant="h3" style={{ fontWeight: 700 }}>{analytics.totalSales.toFixed(2)} BD</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statCard}>
                            <Box className={classes.iconShape} style={{ backgroundColor: theme.palette.info.main }}>
                                <ShoppingCart />
                            </Box>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>TOTAL ORDERS</Typography>
                            <Typography variant="h3" style={{ fontWeight: 700 }}>{analytics.totalOrders}</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statCard}>
                            <Box className={classes.iconShape} style={{ backgroundColor: "#2dce89" }}>
                                <CheckCircle />
                            </Box>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>COMPLETED</Typography>
                            <Typography variant="h3" style={{ fontWeight: 700 }}>{analytics.completedOrders}</Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card className={classes.statCard}>
                            <Box className={classes.iconShape} style={{ backgroundColor: "#fb6340" }}>
                                <HourglassEmpty />
                            </Box>
                            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>PENDING</Typography>
                            <Typography variant="h3" style={{ fontWeight: 700 }}>{analytics.pendingOrders}</Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters Row */}
                <Box className={classes.filterBox}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                placeholder="Search by Order ID or Customer..."
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search color="disabled" /></InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                select
                                fullWidth
                                label="Branch"
                                variant="outlined"
                                size="small"
                                value={branchFilter}
                                onChange={(e) => setBranchFilter(e.target.value)}
                                SelectProps={{ native: true }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <option value="all">All Branches</option>
                                {branches.map((branch, index) => (
                                    <option key={index} value={branch}>
                                        {branch}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                select
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                SelectProps={{ native: true }}
                            >
                                <option value="all">All Statuses</option>
                                <option value="0">Placed</option>
                                <option value="1">Washing</option>
                                <option value="2">Washed</option>
                                <option value="3">Completed</option>
                                <option value="4">Cancelled</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="From"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                label="To"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Orders Table */}
                <Card className={classes.cardRoot}>
                    <TableContainer component={Paper} elevation={0}>
                        <Table>
                            <TableHead style={{ backgroundColor: "#f6f9fc" }}>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'id'}
                                            direction={sortConfig.key === 'id' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('id')}
                                        >
                                            ORDER ID
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'placedOn'}
                                            direction={sortConfig.key === 'placedOn' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('placedOn')}
                                        >
                                            DATE
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'customer'}
                                            direction={sortConfig.key === 'customer' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('customer')}
                                        >
                                            CUSTOMER
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'total'}
                                            direction={sortConfig.key === 'total' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('total')}
                                        >
                                            TOTAL
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>
                                        <TableSortLabel
                                            active={sortConfig.key === 'status'}
                                            direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('status')}
                                        >
                                            STATUS
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 700, color: "#8898aa" }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" style={{ padding: '60px' }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : filteredOrders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" style={{ padding: '60px' }}>
                                            <Typography variant="body1" color="textSecondary">No orders found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredOrders.map((order) => {
                                    const statusInfo = getStatusInfo(order.status);
                                    return (
                                        <TableRow key={order.id} hover>
                                            <TableCell style={{ fontWeight: 600 }}>#{order.id}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                    {new Date(order.placedOn).toLocaleDateString()}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {new Date(order.placedOn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const customer = getCustomer(order);
                                                    return customer ? (
                                                        <Box display="flex" alignItems="center">
                                                            <Typography variant="body2" style={{ fontWeight: 600, marginRight: '8px' }}>
                                                                {customer.mobileNumber}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedCustomer(customer);
                                                                    setCustomerDialogOpen(true);
                                                                }}
                                                            >
                                                                <Person fontSize="small" color="primary" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : `ID: ${order.walkInCustomerId}`;
                                                })()}
                                            </TableCell>
                                            <TableCell className={classes.priceCell}>
                                                {(order.total || 0).toFixed(2)} BD
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={statusInfo.label}
                                                    className={classes.statusChip}
                                                    style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        style={{ textTransform: 'none', fontWeight: 600, marginRight: '8px' }}
                                                        onClick={() => handleViewDetails(order)}
                                                    >
                                                        View Items
                                                    </Button>

                                                    {order.status === 0 && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="primary"
                                                            style={{ borderRadius: '6px', fontSize: '0.75rem', marginRight: '8px' }}
                                                            onClick={() => updateOrderStatus(order.id, 1)}
                                                        >
                                                            Start Washing
                                                        </Button>
                                                    )}
                                                    {order.status === 1 && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="primary"
                                                            style={{ borderRadius: '6px', fontSize: '0.75rem', marginRight: '8px' }}
                                                            onClick={() => updateOrderStatus(order.id, 2)}
                                                        >
                                                            Mark Washed
                                                        </Button>
                                                    )}
                                                    {order.status === 2 && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="primary"
                                                            style={{ borderRadius: '6px', fontSize: '0.75rem', marginRight: '8px' }}
                                                            onClick={() => updateOrderStatus(order.id, 3)}
                                                        >
                                                            Complete
                                                        </Button>
                                                    )}

                                                    <IconButton size="small" onClick={(e) => {
                                                        setAnchorEl(e.currentTarget);
                                                        setActiveOrderId(order.id);
                                                    }}>
                                                        <MoreVert fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Container>

            {/* Status Change Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 0)}>Set to Placed</MenuItem>
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 1)}>Set to Washing</MenuItem>
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 2)}>Set to Washed</MenuItem>
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 3)}>Set to Completed</MenuItem>
                <MenuItem onClick={() => updateOrderStatus(activeOrderId, 4)}>Set to Cancelled</MenuItem>
            </Menu>

            {/* Customer Details Dialog */}
            <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h4" style={{ fontWeight: 700 }}>Customer Details</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedCustomer && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Full Name</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                                        {selectedCustomer.firstName} {selectedCustomer.lastName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Mobile Number</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                                        {selectedCustomer.mobileNumber}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Email Address</Typography>
                                    <Typography variant="body1">
                                        {selectedCustomer.emailAddress || "-"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                                    <Typography variant="body1">
                                        {[
                                            selectedCustomer.building ? `Building ${selectedCustomer.building}` : null,
                                            selectedCustomer.road ? `Road ${selectedCustomer.road}` : null,
                                            selectedCustomer.block ? `Block ${selectedCustomer.block}` : null,
                                            selectedCustomer.districtArea,
                                            selectedCustomer.flatOfficeNo ? `Flat/Office ${selectedCustomer.flatOfficeNo}` : null
                                        ].filter(Boolean).join(", ")}
                                    </Typography>
                                    {selectedCustomer.shortAddress && (
                                        <Typography variant="caption" color="textSecondary">
                                            ({selectedCustomer.shortAddress})
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustomerDialogOpen(false)} color="primary">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Order Details Dialog */}
            <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h4" style={{ fontWeight: 700 }}>Order Details #{selectedOrder?.id}</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedOrder && (() => {
                        const customer = getCustomer(selectedOrder);
                        return (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom color="primary">CUSTOMER INFO</Typography>
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                    {customer?.firstName} {customer?.lastName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">{customer?.mobileNumber}</Typography>
                                <Typography variant="body2" style={{ marginBottom: '1.5rem' }}>
                                    {[customer?.building, customer?.road, customer?.block].filter(Boolean).join(", ")}
                                </Typography>

                                <Typography variant="subtitle2" gutterBottom color="primary">ORDER ITEMS</Typography>
                                {selectedOrder.walkInOrderItemsById?.map((item, idx) => (
                                    <Box key={idx} className={classes.orderItemRow}>
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                {item.serviceByServiceId?.nameEn || `Service ID: ${item.service_id || item.serviceId}`}
                                            </Typography>
                                            {item.serviceType?.nameEn && (
                                                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                    {item.serviceType.nameEn}
                                                </Typography>
                                            )}
                                            <Box className={classes.orderItemMeta}>
                                                <Box className={classes.orderItemBadge}>
                                                    Qty {item.quantity}
                                                </Box>
                                                <Box className={classes.orderItemPriceNote}>
                                                    {(item.price || 0).toFixed(2)} BD each
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box textAlign="right">
                                            <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                                                Line Total
                                            </Typography>
                                            <Typography variant="body2" style={{ fontWeight: 700 }}>
                                                {(item.total || 0).toFixed(2)} BD
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}

                                <Divider style={{ margin: '1.5rem 0' }} />

                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2">Subtotal</Typography>
                                    <Typography variant="body2">{(selectedOrder.subtotal || 0).toFixed(2)} BD</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2">Discount</Typography>
                                    <Typography variant="body2" color="error">-{(selectedOrder.discount || 0).toFixed(2)} BD</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2">Delivery Charge</Typography>
                                    <Typography variant="body2">+{(selectedOrder.deliveryCharge || 0).toFixed(2)} BD</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mt={2}>
                                    <Typography variant="h5" style={{ fontWeight: 700 }}>Total</Typography>
                                    <Typography variant="h4" color="primary" style={{ fontWeight: 800 }}>{(selectedOrder.total || 0).toFixed(2)} BD</Typography>
                                </Box>
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions style={{ padding: '1rem' }}>
                    <Button onClick={() => setDetailsOpen(false)} color="default">Close</Button>
                    {selectedOrder?.status !== 3 && (
                        <Button variant="contained" color="primary" onClick={() => updateOrderStatus(selectedOrder.id, 3)}>
                            Mark as Completed
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default PosSales;
